import { useCallback, useContext, useEffect } from 'react';

import type { ApolloError } from '@apollo/client';
import { useQuery } from '@apollo/client';

import sessionDialogOpenSubject from 'AuthenticatedRoutesContainer/TokenWatcher/sessionDialogOpenSubject';
import { ApolloFetchPolicy } from 'common/apollo/constants';
import { useObservableState } from 'common/components/core/hooks/useObservableState';
import { OfflineStateContext } from 'common/components/OfflineState/context';
import usePollingContextSubscription from 'common/components/withPollingContext/usePollingContextSubscription';
import { WebSocketConnectionState } from 'common/constants/webSocketConnectionState';
import { useWebSocketConnectionState } from 'common/helpers/webSocketState';

import { useEvent } from '../useEvent/useEvent';
import { isDisconnected } from './helpers';
import type { HeartbeatResponse } from './queries';
import { HeartbeatQuery } from './queries';

const HEARTBEAT_INTERVAL = 15_000;

export default function useHeartbeat() {
  const { isOffline, setIsOffline } = useContext(OfflineStateContext);

  const onCompleted = useEvent(() => {
    setIsOffline(false);
  });

  const onError = useEvent((error: ApolloError) => {
    if (isDisconnected(error) && !isOffline) {
      setIsOffline(true);
    }
  });

  const webSocketState = useWebSocketConnectionState();
  const isSessionDialogOpen = useObservableState(sessionDialogOpenSubject);
  const skip = (webSocketState === WebSocketConnectionState.OPEN && !isOffline) || isSessionDialogOpen;

  const { startPolling, stopPolling } = useQuery<HeartbeatResponse>(HeartbeatQuery, {
    pollInterval: HEARTBEAT_INTERVAL,
    onCompleted,
    onError,
    fetchPolicy: ApolloFetchPolicy.NO_CACHE,
    notifyOnNetworkStatusChange: true,
    skip,
  });

  // There is a bug in apollo client when changing the value of the `skip` option
  // Thus, we need to manually call start/stop polling
  useEffect(() => {
    if (webSocketState === WebSocketConnectionState.NOT_AVAILABLE) {
      return;
    }
    if (skip) stopPolling();
    else startPolling(HEARTBEAT_INTERVAL);
  }, [skip, startPolling, stopPolling, webSocketState]);

  const boundStartPolling = useCallback(() => startPolling(HEARTBEAT_INTERVAL), [startPolling]);
  usePollingContextSubscription({ startActions: [boundStartPolling], stopActions: [stopPolling] });
}
